import { isArray } from "lodash";
import { PlatformType } from "@/types/platform.type";
import axios from "@/libs/axios";
import { nextTick } from "@vue/composition-api";
import useMultiSelect from "@/composables/v2.1/useMultiSelect";
// @ts-ignore
import i18n from "@/libs/i18n";
import { BvModal } from "bootstrap-vue";
import useActiveChat from "@/composables/useActiveChat";
// @ts-ignore
import useReadChat from "@/composables/v2.1/useReadChat";
import useChats from "@/composables/useChats";
// @ts-ignore
import router from "@/router";
// @ts-ignore
import toastificationContent from "@/layouts/components/toastification/toastificationContent";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import MultiSelectChatInterface from "@/interfaces/multiSelectChat.interface";

export default function useDeleteChat(bvModal: BvModal, toast: any) {
    const { inboxModulePath, publicApiEndpointPath } = useInboxModuleType();
    const deleteResults = { ok: [], error: [] };

    function deleteChat(chat: { id: number; identityId: number; platform: PlatformType }, deleteByIdentity = false) {
        bvModal
            .msgBoxConfirm(i18n.t("confirm.Are you sure?"), {
                title: i18n.t("confirm.Are you sure?"),
                cancelTitle: i18n.t("confirm.Cancel"),
                cancelVariant: "light",
                okTitle: i18n.t("confirm.Yes, delete"),
                hideHeaderClose: false,
                headerCloseVariant: "light",
                centered: true,
            })
            .then(async (result) => {
                if (result) {
                    const { findActiveChatLastConversation, closeActiveChat } = useActiveChat();

                    const activeChatId = findActiveChatLastConversation()?.data.id;
                    const chats = isArray(chat) ? chat : [chat];

                    const deleteEndpoint = deleteByIdentity
                        ? `${publicApiEndpointPath.value}/identity/${chat.identityId}`
                        : `${publicApiEndpointPath.value}/${chat.id}`;

                    const { setRead } = useReadChat(toast);
                    await Promise.all(
                        chats.map(async (chat) => {
                            // @ts-ignore
                            await axios
                                .delete(deleteEndpoint)
                                .then(() => {
                                    setRead(
                                        <MultiSelectChatInterface>{
                                            id: chat.id,
                                            channel: {
                                                platform: chat.platform,
                                            },
                                        },
                                        { showToast: false }
                                    );

                                    nextTick(() => {
                                        const { deleteChatFromChatsList } = useChats();

                                        deleteChatFromChatsList(chat.id);
                                        closeActiveChat(chat.id);
                                    });

                                    // @ts-ignore
                                    deleteResults.ok.push(chat);
                                })
                                // @ts-ignore
                                .catch(() => deleteResults.error.push(chat));
                        })
                    );

                    let status = "success";
                    let notificationText = i18n.t("toast.delete.success-amount", {
                        entity: i18n.tc("entities.conversation.conversations", deleteResults.ok.length),
                        amount: deleteResults.ok.length,
                    });

                    if (deleteResults.ok.length > 0 && deleteResults.error.length > 0) {
                        status = "warning";
                        notificationText = i18n.t("toast.delete.multiple-amount", {
                            entityOk: i18n.tc("entities.conversation.conversations", deleteResults.ok.length),
                            amountOk: deleteResults.ok.length,
                            entityError: i18n.tc("entities.conversation.conversations", deleteResults.error.length),
                            amountError: deleteResults.error.length,
                        });
                    } else if (deleteResults.ok.length === 0 && deleteResults.error.length > 0) {
                        status = "error";
                        notificationText = i18n.t("toast.delete.error-amount", {
                            entity: i18n.tc("entities.conversation.conversations", deleteResults.error.length),
                            amount: deleteResults.error.length,
                        });
                    }

                    const { multiSelectEnabled, clearMultiSelect } = useMultiSelect();
                    if (multiSelectEnabled.value) {
                        clearMultiSelect(deleteResults.ok);
                    }

                    // Closed already, so just redirect to inbox.
                    if (activeChatId || chats.find((chat) => chat.id === activeChatId)) {
                        router.replace({
                            name: inboxModulePath?.value,
                        });
                    }

                    toast({
                        component: toastificationContent,
                        props: {
                            title: i18n.t("Delete"),
                            text: notificationText,
                            icon: status === "error" ? "times" : "check",
                            variant: status,
                        },
                    });
                }
            });

        return deleteResults;
    }

    return {
        deleteChat,
    };
}
